import { getToken } from '@/lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WeatherDay {
  date: string;
  maxTemp: number;
  minTemp: number;
  precipitation: number; // inches
  weatherCode: number;
  description: string;
  feelsLike?: number; // average apparent temp during daylight hours (8am–6pm)
}

export interface WeatherData {
  location: string;
  lat: number;
  lon: number;
  currentTemp: number;
  currentDescription: string;
  today: WeatherDay;
  forecast: WeatherDay[]; // next 10 days
  recentRainfall: number; // total inches in last 5 days
  frostRisk: {
    date: string;
    minTemp: number;
  } | null;
  lastUpdated: string; // ISO timestamp
}

// ── Weather code mappings ─────────────────────────────────────────────────────

const WEATHER_CODE_DESCRIPTIONS: Record<number, string> = {
  0:  'Clear sky',
  1:  'Mainly clear',
  2:  'Partly cloudy',
  3:  'Overcast',
  45: 'Foggy',
  48: 'Icy fog',
  51: 'Light drizzle',
  53: 'Moderate drizzle',
  55: 'Dense drizzle',
  61: 'Slight rain',
  63: 'Moderate rain',
  65: 'Heavy rain',
  71: 'Slight snow',
  73: 'Moderate snow',
  75: 'Heavy snow',
  80: 'Slight showers',
  81: 'Moderate showers',
  82: 'Heavy showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with hail',
  99: 'Thunderstorm with heavy hail',
};

function getWeatherDescription(code: number): string {
  return WEATHER_CODE_DESCRIPTIONS[code] ?? 'Variable conditions';
}

export function getWeatherIcon(weatherCode: number): string {
  if (weatherCode <= 2)                              return '☀️';
  if (weatherCode === 3)                             return '☁️';
  if (weatherCode >= 45 && weatherCode <= 48)        return '🌫️';
  if (weatherCode >= 51 && weatherCode <= 55)        return '🌦️';
  if (weatherCode >= 61 && weatherCode <= 65)        return '🌧️';
  if (weatherCode >= 71 && weatherCode <= 75)        return '❄️';
  if (weatherCode >= 80 && weatherCode <= 82)        return '🌧️';
  if (weatherCode >= 95 && weatherCode <= 99)        return '⛈️';
  return '🌤️';
}

async function getRecentRainfall(lat: number, lon: number): Promise<number> {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const fiveCompletedDaysAgo = new Date(yesterday)
  fiveCompletedDaysAgo.setDate(yesterday.getDate() - 4)

  const startDate = fiveCompletedDaysAgo.toISOString().split('T')[0]
  const endDate = yesterday.toISOString().split('T')[0]

  const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${startDate}&end_date=${endDate}&daily=precipitation_sum&precipitation_unit=inch&timezone=auto`

  try {
    const res = await fetch(url)
    if (!res.ok) return 0
    const data = await res.json()

    const dailyPrecip: number[] = data.daily?.precipitation_sum ?? []
    const total = dailyPrecip.reduce((sum: number, val: number) => sum + (val ?? 0), 0)
    console.log('Recent rainfall (last 5 completed days):', total, 'inches', dailyPrecip)
    return total
  } catch (e) {
    console.error('Failed to fetch rainfall history:', e)
    return 0
  }
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function getCoordinatesFromZip(
  zip: string
): Promise<{ lat: number; lon: number; name: string } | null> {
  try {
    const res = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(zip)}&count=1&language=en&format=json`,
      { signal: AbortSignal.timeout(8000) }
    );
    if (!res.ok) return null;
    const data = await res.json() as {
      results?: Array<{
        latitude: number;
        longitude: number;
        name: string;
        admin1?: string;
      }>;
    };
    if (!data.results || data.results.length === 0) return null;
    const r = data.results[0];
    const name = r.admin1 ? `${r.name}, ${r.admin1}` : r.name;
    return { lat: r.latitude, lon: r.longitude, name };
  } catch {
    return null;
  }
}

export async function fetchWeatherData(
  lat: number,
  lon: number,
  locationName: string
): Promise<WeatherData> {
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode` +
    `&hourly=temperature_2m,precipitation,apparent_temperature` +
    `&current_weather=true` +
    `&temperature_unit=fahrenheit` +
    `&precipitation_unit=inch` +
    `&timezone=auto` +
    `&forecast_days=10`;

  const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
  if (!res.ok) throw new Error(`Weather fetch failed: ${res.status}`);

  const data = await res.json() as {
    current_weather: { temperature: number; weathercode: number };
    daily: {
      time: string[];
      temperature_2m_max: number[];
      temperature_2m_min: number[];
      precipitation_sum: number[];
      weathercode: number[];
    };
    hourly: {
      time: string[];
      temperature_2m: number[];
      precipitation: number[];
      apparent_temperature: number[];
    };
  };

  const { current_weather, daily, hourly } = data;

  // Build forecast days array
  const forecast: WeatherDay[] = daily.time.map((date, i) => {
    // Calculate average apparent temperature during daylight hours (8am–6pm)
    const dayHourIndices: number[] = [];
    hourly.time.forEach((hTime, idx) => {
      if (hTime.startsWith(date)) {
        const hour = parseInt(hTime.split('T')[1].split(':')[0], 10);
        if (hour >= 8 && hour < 18) dayHourIndices.push(idx);
      }
    });
    let feelsLike: number | undefined;
    if (dayHourIndices.length > 0) {
      const avg = dayHourIndices.reduce((sum, idx) => sum + (hourly.apparent_temperature[idx] ?? 0), 0) / dayHourIndices.length;
      feelsLike = Math.round(avg);
    }

    return {
      date,
      maxTemp: daily.temperature_2m_max[i] ?? 0,
      minTemp: daily.temperature_2m_min[i] ?? 0,
      precipitation: Math.round((daily.precipitation_sum[i] ?? 0) * 100) / 100,
      weatherCode: daily.weathercode[i] ?? 0,
      description: getWeatherDescription(daily.weathercode[i] ?? 0),
      feelsLike,
    };
  }).sort((a, b) => a.date.localeCompare(b.date));

  // Calculate recentRainfall: sum historical precipitation in the last 5 days
  const recentRainfall = Math.round((await getRecentRainfall(lat, lon)) * 100) / 100;

  // Calculate frost risk: first day in next 10 with minTemp <= 32F
  let frostRisk: WeatherData['frostRisk'] = null;
  for (let i = 0; i < Math.min(10, forecast.length); i++) {
    if (forecast[i].minTemp <= 32) {
      frostRisk = { date: forecast[i].date, minTemp: forecast[i].minTemp };
      break;
    }
  }

  return {
    location: locationName,
    lat,
    lon,
    currentTemp: current_weather.temperature,
    currentDescription: getWeatherDescription(current_weather.weathercode),
    today: forecast[0],
    forecast,
    recentRainfall,
    frostRisk,
    lastUpdated: new Date().toISOString(),
  };
}

// ── Cache helpers ─────────────────────────────────────────────────────────────

const CACHE_KEY = 'garden-weather-cache-v3';
export const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

interface WeatherCache {
  zip: string;
  data: WeatherData;
  fetchedAt: string;
}

interface WeatherCacheReadResult {
  data: WeatherData;
  fetchedAt: Date;
}

export interface GetWeatherForUserOptions {
  bypassCache?: boolean;
  onGeocodingFailure?: () => void;
}

function normalizeZipForCache(zip: string): string {
  return zip.trim();
}

function saveCachedWeather(data: WeatherData, zip: string): void {
  if (typeof window === 'undefined') return;
  try {
    const cache: WeatherCache = { zip: normalizeZipForCache(zip), data, fetchedAt: new Date().toISOString() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // quota exceeded — fail silently
  }
}

function getZipFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    // Primary: dedicated zip key (as specified)
    const direct = localStorage.getItem('garden-zone-zip');
    if (direct) return direct;
    // Fallback: zone data object which stores zip alongside zone number
    const raw = localStorage.getItem('garden-user-zone');
    if (!raw) return null;
    const zoneData = JSON.parse(raw) as { zone?: number; zip?: string };
    return zoneData.zip ?? null;
  } catch {
    return null;
  }
}

// ── Main entry point ──────────────────────────────────────────────────────────

export function getCachedWeatherWithAge(expectedZip?: string | null): WeatherCacheReadResult | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw) as Partial<WeatherCache> & { cachedAt?: string };
    const normalizedExpectedZip = expectedZip ? normalizeZipForCache(expectedZip) : null;
    if (normalizedExpectedZip && cache.zip !== normalizedExpectedZip) return null;
    const ts = cache.fetchedAt ?? cache.cachedAt;
    if (!cache.data || !ts) return null;
    const fetchedAt = new Date(ts);
    if (Number.isNaN(fetchedAt.getTime())) return null;
    return { data: cache.data, fetchedAt };
  } catch {
    return null;
  }
}

export async function getWeatherForUser(options: GetWeatherForUserOptions = {}): Promise<WeatherData | null> {
  let zip: string | null = null;

  // Try 1: get zip from user account via API
  const token = getToken();
  if (token) {
    try {
      const res = await fetch('/api/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json() as { user?: { zip?: string | null } };
        zip = data.user?.zip ?? null;
      }
    } catch (e) {
      console.error('Failed to fetch user for zip:', e);
    }
  }

  // Try 2: fall back to localStorage
  if (!zip) {
    zip = getZipFromStorage();
  }

  // No zip found — return null, do not prompt
  if (!zip) return null;
  const normalizedZip = normalizeZipForCache(zip);

  // Check cache first unless the caller explicitly wants a fresh forecast.
  if (!options.bypassCache) {
    const cached = getCachedWeatherWithAge(normalizedZip);
    if (cached) {
      const ageMs = Date.now() - cached.fetchedAt.getTime();
      if (ageMs < CACHE_TTL_MS) return cached.data;
    }
  }

  // Fetch fresh weather
  try {
    const coords = await getCoordinatesFromZip(zip);
    if (!coords) {
      options.onGeocodingFailure?.();
      return null;
    }

    const weather = await fetchWeatherData(coords.lat, coords.lon, coords.name);
    saveCachedWeather(weather, normalizedZip);
    return weather;
  } catch (e) {
    console.error('Weather fetch failed:', e);
    return null;
  }
}
